import type { Chamado, ChamadoLocal } from "../types";
import { urlDeConfig, type SessaoWidget } from "./sessao";

/**
 * Persistência local dos chamados abertos NESTE browser (DEV-107). É o que faz o painel
 * "Meus chamados" existir sem login: o token de cada chamado vem no 202 do intake e só
 * aqui ele fica guardado.
 *
 * Três decisões que o formato carrega:
 *
 * 1. **Namespace por relator.** A única chave que o widget tinha (`altbug:fabpos`) é
 *    global, então dois relatores no mesmo domínio a compartilham. Para posição de botão
 *    é inofensivo; para credencial de chamado, não — o painel de um mostraria (e tentaria
 *    abrir) chamado do outro, com 404 garantido. A chave leva um sufixo derivado do token.
 * 2. **Versão no envelope.** Mudar a forma sem versão deixa o widget lendo objeto antigo
 *    e quebrando na primeira propriedade que não existe; aqui versão diferente é
 *    descartada inteira (é cache de conveniência, não fonte de verdade — o servidor tem
 *    tudo).
 * 3. **Teto de itens.** Sem teto, um site que o usuário visita todo dia acumula chamados
 *    para sempre e o resumo (que tem limite de 20 tokens no servidor) passaria a truncar
 *    silenciosamente os mais recentes. Guardamos os N mais recentes.
 *
 * Tudo é `try/catch`: localStorage pode estar indisponível (modo privado, cookies
 * bloqueados, quota estourada) e nada disso pode impedir o report de funcionar.
 */

const VERSAO = 1;
const PREFIXO = "altbug:chamados";

/** Máximo de chamados guardados. Casa com o teto de tokens do POST /chamados/resumo:
    guardar mais do que o servidor aceita num lote faria o excedente nunca aparecer. */
export const MAX_CHAMADOS = 20;

interface Envelope {
  v: number;
  itens: ChamadoLocal[];
  /** Sessão do login pelo popup (DEV-109), quando há. */
  sessao?: SessaoWidget;
}

/**
 * Chave do storage para um token de widget. Usa só o PREFIXO do token (12 chars), não o
 * token inteiro: já é único entre relatores e evita espalhar a credencial do widget por
 * mais um lugar do que o necessário. (O token não é segredo — é semi-público, estilo DSN
 * do Sentry —, mas não custa nada não repeti-lo.)
 */
export function chaveDe(widgetToken: string): string {
  return `${PREFIXO}:${widgetToken.slice(0, 12)}`;
}

/** O envelope cru, já validado. Base de todas as leituras. */
function lerEnvelope(widgetToken: string): Envelope {
  try {
    const raw = localStorage.getItem(chaveDe(widgetToken));
    if (!raw) return { v: VERSAO, itens: [] };
    const env = JSON.parse(raw) as Partial<Envelope> | null;
    if (!env || env.v !== VERSAO || !Array.isArray(env.itens)) return { v: VERSAO, itens: [] };
    // A `configUrl` é repeneirada na LEITURA, e não só quando chega do popup: este
    // storage é do site anfitrião, que pode reescrevê-lo — e é dela que sai o destino de
    // um `window.open` disparado pelo nosso menu (DEV-223).
    const s = env.sessao;
    const sessao =
      s && typeof s === "object" && typeof s.sessao === "string" && s.sessao !== ""
        ? { ...s, configUrl: urlDeConfig(s.configUrl) }
        : undefined;

    return { v: VERSAO, itens: env.itens.filter(valido), ...(sessao ? { sessao } : {}) };
  } catch {
    return { v: VERSAO, itens: [] };
  }
}

function gravarEnvelope(widgetToken: string, env: Envelope): void {
  try {
    localStorage.setItem(
      chaveDe(widgetToken),
      JSON.stringify({ v: VERSAO, itens: env.itens.slice(0, MAX_CHAMADOS), ...(env.sessao ? { sessao: env.sessao } : {}) }),
    );
  } catch {
    /* localStorage indisponível ou cheio — o chamado segue existindo no servidor */
  }
}

/** Lê os chamados guardados. Storage ausente, JSON corrompido ou versão diferente → []. */
export function lerChamados(widgetToken: string): ChamadoLocal[] {
  return lerEnvelope(widgetToken).itens;
}

/**
 * Os chamados como estão no disco AGORA, para o poll trocar a cópia em memória (DEV-358).
 *
 * O widget lê o storage no `init` e dali em diante confia na cópia dele — mas o storage é
 * dividido entre as abas do site. Quem lia a resposta numa aba gravava as vistas lá, e a
 * outra seguia com as de quando carregou: a resposta seguinte do próprio cliente fazia o
 * total do servidor passar delas, e a badge acendia com a conversa inteira, a fala dele
 * incluída. Relendo a cada poll, a outra aba acompanha em até um tick.
 *
 * `null`, e não `[]`, quando o storage não responde: aí a cópia em memória é a ÚNICA
 * lista que existe (o chamado recém-aberto num site com storage bloqueado só mora nela),
 * e trocá-la por vazio o tiraria da lista no tick seguinte.
 */
export function relerChamados(widgetToken: string): ChamadoLocal[] | null {
  try {
    localStorage.getItem(chaveDe(widgetToken));
  } catch {
    return null;
  }

  return lerChamados(widgetToken);
}

/** Grava a lista (já cortada no teto), preservando a sessão. */
export function gravarChamados(widgetToken: string, itens: ChamadoLocal[]): void {
  gravarEnvelope(widgetToken, { ...lerEnvelope(widgetToken), itens });
}

/* ---------------- Sessão (DEV-109) ---------------- */

export function lerSessao(widgetToken: string): SessaoWidget | null {
  return lerEnvelope(widgetToken).sessao ?? null;
}

export function gravarSessao(widgetToken: string, sessao: SessaoWidget): void {
  gravarEnvelope(widgetToken, { ...lerEnvelope(widgetToken), sessao });
}

/**
 * Esquece a sessão (saiu, ou o servidor disse que ela venceu) — e, com ela, os chamados
 * que SÓ existiam por identidade: sem token local eles não podem mais ser abertos, e
 * deixá-los na lista renderizaria linhas que dão 401 no clique.
 */
export function esquecerSessao(widgetToken: string): ChamadoLocal[] {
  const env = lerEnvelope(widgetToken);
  const itens = env.itens.filter((c) => c.token !== "");
  gravarEnvelope(widgetToken, { v: VERSAO, itens });

  return itens;
}

/**
 * Registra os chamados que o servidor devolveu por IDENTIDADE (sessão) e que não estavam
 * no storage — os abertos em outro aparelho e os cards que o time COMPARTILHOU (DEV-245).
 * Guarda sem token: quem os abre é a sessão.
 *
 * `respostasVistas` nasce igual ao total, não zero. Quem acaba de logar não deve receber
 * uma badge com todas as respostas de meses de histórico — muitas já foram lidas no
 * portal. A promessa da badge é "eu te aviso do que chegar de novo".
 *
 * **O card compartilhado é a exceção, e nasce em 0.** Ali não há histórico a poupar: ele
 * chega porque alguém do time acabou de abri-lo para esta pessoa, e a resposta ao cliente
 * já escrita costuma ser justamente o pedido que ela precisa ler. Zerar as vistas é o que
 * faz a badge apontar para ele — sem isso o card entraria mudo na lista, e a pessoa só
 * descobriria abrindo o painel por outro motivo.
 */
export function registrarDaSessao(widgetToken: string, remotos: Chamado[]): ChamadoLocal[] {
  const env = lerEnvelope(widgetToken);
  const conhecidos = new Set(env.itens.map((c) => c.codigo));
  const novos = remotos
    .filter((r) => !conhecidos.has(r.codigo))
    .map<ChamadoLocal>((r) => ({
      codigo: r.codigo,
      token: "",
      titulo: r.titulo,
      abertoEm: r.abertoEm,
      respostasVistas: r.compartilhado ? 0 : r.respostas,
      compartilhado: r.compartilhado,
    }));
  if (novos.length === 0) return env.itens;

  const itens = cortarNoTeto([...env.itens, ...novos], remotos);
  gravarEnvelope(widgetToken, { ...env, itens });

  return itens;
}

/**
 * A união (o que estava guardado + o que chegou agora) na ordem do SERVIDOR, cortada no
 * teto. É o corte que precisa ser DETERMINÍSTICO, e é onde estava o bug.
 *
 * Antes era `[...novos, ...guardados].slice(0, MAX)`: com o servidor devolvendo mais que o
 * teto, os do fim caíam fora — e no poll seguinte voltavam a ser "novos", entravam na
 * frente e derrubavam outros. A lista girava a cada 15s, a badge oscilava junto, e o
 * compartilhado que dava a volta reentrava com `respostasVistas: 0`, reacendendo aviso de
 * resposta que já tinha sido lida. Ordenar pela resposta do servidor faz a mesma entrada
 * produzir sempre a mesma saída: quem está fora do teto continua fora, quem está dentro
 * continua dentro, e a lista só muda quando o servidor muda.
 *
 * **Token local nunca é despejado pelo teto.** Ele é a ÚNICA credencial daquele chamado
 * neste browser (o 202 do intake o emite uma vez): perdê-lo é perder o chamado, inclusive
 * para quem depois sai da sessão — `esquecerSessao` guarda justamente os que têm token. Por
 * isso eles são reservados primeiro, e a atividade recente preenche o que sobra.
 *
 * O que sai daqui é também a ordem em que o painel PINTA a lista, e ela passa a ser a da
 * resposta — atividade mais recente primeiro, que é o que `separarChamados` já supunha.
 * Só quando há novidade, como antes: sem nada novo o storage não é reescrito.
 */
function cortarNoTeto(uniao: ChamadoLocal[], remotos: Chamado[]): ChamadoLocal[] {
  const ordem = new Map(remotos.map((r, i) => [r.codigo, i]));
  // Chamado que o servidor não citou vai para o fim: ou ele está de saída (o
  // `esquecerChamados` do widget o remove logo em seguida), ou é local e o teto não o corta.
  const posicao = (c: ChamadoLocal): number => ordem.get(c.codigo) ?? Number.MAX_SAFE_INTEGER;
  const ordenados = [...uniao].sort((a, b) => posicao(a) - posicao(b));
  if (ordenados.length <= MAX_CHAMADOS) return ordenados;

  const fica = new Set(
    ordenados
      .filter((c) => c.token !== "")
      .slice(0, MAX_CHAMADOS)
      .map((c) => c.codigo),
  );
  for (const c of ordenados) {
    if (fica.size >= MAX_CHAMADOS) break;
    fica.add(c.codigo);
  }

  return ordenados.filter((c) => fica.has(c.codigo));
}

/**
 * Acrescenta o chamado recém-aberto no TOPO e devolve a lista nova.
 *
 * Dedup por código: reenviar o mesmo report não duplica a linha, e o token novo
 * SUBSTITUI o antigo (se o servidor reemitiu, é o novo que abre o chamado).
 * `respostasVistas` nasce em 0 — quem abriu ainda não viu resposta nenhuma.
 */
export function registrarChamado(
  widgetToken: string,
  novo: { codigo: string; token: string; titulo: string; abertoEm?: string },
): ChamadoLocal[] {
  const item: ChamadoLocal = {
    codigo: novo.codigo,
    token: novo.token,
    titulo: novo.titulo,
    abertoEm: novo.abertoEm ?? new Date().toISOString(),
    respostasVistas: 0,
  };
  const resto = lerChamados(widgetToken).filter((c) => c.codigo !== item.codigo);
  const itens = [item, ...resto].slice(0, MAX_CHAMADOS);
  gravarChamados(widgetToken, itens);
  return itens;
}

/**
 * Marca as respostas de um chamado como vistas (zera a badge dele). O total vem do
 * servidor, não de uma contagem local: se o time excluir um comentário o total DIMINUI,
 * e um contador local que só soubesse incrementar deixaria a badge acesa para sempre.
 */
export function marcarVistas(widgetToken: string, codigo: string, respostas: number): ChamadoLocal[] {
  const itens = lerChamados(widgetToken).map((c) =>
    c.codigo === codigo ? { ...c, respostasVistas: Math.max(0, respostas) } : c,
  );
  gravarChamados(widgetToken, itens);
  return itens;
}

/**
 * Re-chaveia os itens cujo chamado MUDOU DE CÓDIGO (DEV-302), preservando o resto da
 * linha — sobretudo o `token`, que é a credencial do caminho anônimo.
 *
 * Isto tem de rodar ANTES de `esquecerChamados` no mesmo ciclo. A ordem é a feature:
 * o resumo é resolvido por token e volta com o código novo, então o item antigo não casa
 * com nada e o esquecimento o apagaria — com o token dentro. O cliente perderia o acesso
 * ao próprio chamado, sem erro nenhum na tela.
 *
 * `respostasVistas` acompanha de propósito: quem já leu as respostas não deve receber uma
 * badge acesa só porque o time moveu o card de espaço.
 */
export function renomearChamados(widgetToken: string, pares: Array<{ de: string; para: string }>): ChamadoLocal[] {
  if (pares.length === 0) return lerChamados(widgetToken);

  const env = lerEnvelope(widgetToken);
  const novoPor = new Map(pares.map((p) => [p.de, p.para]));
  const itens: ChamadoLocal[] = [];
  let mudou = false;

  for (const item of env.itens) {
    const novo = novoPor.get(item.codigo);
    if (novo !== undefined) mudou = true;
    const renomeado = novo === undefined ? item : { ...item, codigo: novo };

    /* Dedup: o mesmo chamado pode estar guardado duas vezes depois do renome — uma linha
       com o código antigo (e o token) e outra que entrou pela SESSÃO já com o código novo
       e `token: ""`. Fica a que TEM token: sem ela a pessoa passa a depender da sessão, e
       o dia em que a sessão vencer o chamado some do browser dela. */
    const jaTem = itens.findIndex((i) => i.codigo === renomeado.codigo);
    const existente = jaTem >= 0 ? itens[jaTem] : undefined;
    if (existente !== undefined) {
      mudou = true;
      if (existente.token === "" && renomeado.token !== "") itens[jaTem] = renomeado;
      continue;
    }

    itens.push(renomeado);
  }

  if (!mudou) return env.itens;
  gravarEnvelope(widgetToken, { ...env, itens });

  return itens;
}

/** Esquece um chamado (o servidor não o reconhece mais: apagado, ou token revogado). */
export function esquecerChamados(widgetToken: string, codigos: string[]): ChamadoLocal[] {
  if (codigos.length === 0) return lerChamados(widgetToken);
  const fora = new Set(codigos);
  const itens = lerChamados(widgetToken).filter((c) => !fora.has(c.codigo));
  gravarChamados(widgetToken, itens);
  return itens;
}

/**
 * "Manter escondido": o launcher não volta nem recarregando a página.
 *
 * Fica aqui, e não junto de `altbug:fabpos`, por causa do namespace: a posição do botão é
 * uma chave global (dois relatores no mesmo domínio a dividem sem prejuízo), mas esconder
 * UM relator não pode esconder o outro. Vale a mesma regra de credencial — chave por token.
 *
 * A volta é o atalho "?", e é isso que o diálogo de esconder promete: ele LIMPA este flag,
 * senão o launcher reapareceria agora e sumiria de novo no próximo load.
 */
export function lerOculto(widgetToken: string): boolean {
  try {
    return localStorage.getItem(`altbug:oculto:${widgetToken.slice(0, 12)}`) === "1";
  } catch {
    return false;
  }
}

export function gravarOculto(widgetToken: string, oculto: boolean): void {
  const chave = `altbug:oculto:${widgetToken.slice(0, 12)}`;
  try {
    if (oculto) localStorage.setItem(chave, "1");
    else localStorage.removeItem(chave);
  } catch {
    /* localStorage indisponível — ok, só não persiste */
  }
}

/**
 * O que ESTA ABA já anunciou no balão de resposta nova (DEV-216): `{ código: total }`.
 *
 * **`sessionStorage`, não `localStorage`, e a diferença é a feature inteira.** O widget é
 * recriado a cada navegação no site anfitrião; sem marca nenhuma, a mesma resposta velha
 * reapareceria em toda página até alguém abrir o chamado. Em `localStorage` iria longe
 * demais na outra direção — quem volta amanhã, com a resposta ainda por ler, não veria
 * mais nada. `sessionStorage` é exatamente a aba: sobrevive à navegação, morre com ela.
 *
 * O valor guardado é o TOTAL do servidor, não um booleano: a resposta seguinte muda o
 * número e volta a avisar sozinha, pelo mesmo motivo que a badge subtrai em vez de contar.
 *
 * **Nunca guarda texto de resposta** — vale aqui a regra do `ChamadoLocal`: o site
 * anfitrião lê este storage, e conteúdo de chamado não tem por que ficar exposto nele.
 */
function chaveAvisados(widgetToken: string): string {
  return `altbug:avisados:${widgetToken.slice(0, 12)}`;
}

export function lerAvisados(widgetToken: string): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(chaveAvisados(widgetToken));
    if (!raw) return {};
    const mapa = JSON.parse(raw) as Record<string, unknown> | null;
    if (!mapa || typeof mapa !== "object") return {};
    const limpo: Record<string, number> = {};
    for (const [codigo, total] of Object.entries(mapa)) {
      if (typeof total === "number" && Number.isFinite(total)) limpo[codigo] = total;
    }

    return limpo;
  } catch {
    return {};
  }
}

/** Carimba o aviso. O teto acompanha o do storage de chamados: mais códigos do que o
    widget acompanha seria lixo acumulando numa chave que ninguém limpa. */
export function marcarAvisado(widgetToken: string, codigo: string, respostas: number): void {
  try {
    const atual = lerAvisados(widgetToken);
    const entradas = Object.entries({ ...atual, [codigo]: respostas }).slice(-MAX_CHAMADOS);
    sessionStorage.setItem(chaveAvisados(widgetToken), JSON.stringify(Object.fromEntries(entradas)));
  } catch {
    /* sessionStorage indisponível — o balão pode repetir numa navegação, e só */
  }
}

/**
 * Re-chaveia os AVISADOS junto com a lista (DEV-302).
 *
 * Este é o SEGUNDO storage indexado por código, e esquecê-lo não perde credencial
 * nenhuma — produz uma mentira: `aviso.ts` compara `anunciados[codigo] !== respostas`,
 * e com a chave velha órfã a comparação volta a dar verdadeiro, reanunciando num balão
 * uma resposta que a pessoa já leu. Sintoma de aba longa, que teste de load único não vê.
 *
 * Silencioso por construção, como o resto deste par: `sessionStorage` indisponível
 * significa, no pior caso, um balão repetido.
 */
export function renomearAvisados(widgetToken: string, pares: Array<{ de: string; para: string }>): void {
  if (pares.length === 0) return;
  try {
    const atual = lerAvisados(widgetToken);
    let mudou = false;
    for (const { de, para } of pares) {
      const total = atual[de];
      if (total === undefined) continue;
      // O valor do código NOVO vence se já existir: ele veio do mesmo poll, é o mais recente.
      if (!(para in atual)) atual[para] = total;
      delete atual[de];
      mudou = true;
    }
    if (!mudou) return;
    sessionStorage.setItem(chaveAvisados(widgetToken), JSON.stringify(atual));
  } catch {
    /* sessionStorage indisponível — o balão pode repetir numa navegação, e só */
  }
}

/**
 * O e-mail que a pessoa digitou no formulário (DEV-103), para o próximo report vir
 * preenchido. Guardar isto é o que faz "pedir o e-mail" ser uma pergunta única em vez de
 * um pedágio a cada report.
 *
 * Chave por relator, como o `altbug:oculto`: dois relatores no mesmo domínio podem apontar
 * para orgs diferentes, e um default que atravessa não é conveniência, é surpresa.
 *
 * O que fica aqui é o que a PESSOA digitou na própria máquina, num storage que o site
 * anfitrião já lê. Não é identidade verificada em lugar nenhum — a posse do endereço só é
 * provada no login (DEV-109).
 */
export function lerReporterEmail(widgetToken: string): string {
  try {
    const v = localStorage.getItem(`altbug:reporter:${widgetToken.slice(0, 12)}`);

    return typeof v === "string" ? v.trim().slice(0, 320) : "";
  } catch {
    return "";
  }
}

export function gravarReporterEmail(widgetToken: string, email: string): void {
  const chave = `altbug:reporter:${widgetToken.slice(0, 12)}`;
  try {
    const e = email.trim();
    if (e !== "") localStorage.setItem(chave, e.slice(0, 320));
    else localStorage.removeItem(chave);
  } catch {
    /* localStorage indisponível — ok, só não persiste */
  }
}

function valido(c: unknown): c is ChamadoLocal {
  if (!c || typeof c !== "object") return false;
  const o = c as Record<string, unknown>;
  return (
    typeof o.codigo === "string" &&
    o.codigo !== "" &&
    // Token VAZIO é válido: é o chamado que veio pela sessão (aberto em outro aparelho).
    // Quem o abre é a sessão, não um token local.
    typeof o.token === "string" &&
    typeof o.titulo === "string" &&
    typeof o.abertoEm === "string" &&
    typeof o.respostasVistas === "number"
  );
}
